import React, { useEffect } from 'react';
import {
  Grid,
  Box,
  Typography,
  FormControl,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Stack,
  Chip,
  Alert
} from '@mui/material';
import { grey, blue } from '@mui/material/colors';
import { useNavigate, NavLink, useParams } from 'react-router-dom';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { vestResolver } from '@hookform/resolvers/vest';
import { create, test, enforce } from 'vest';
import UICard, { UICardContent, UICardHeading } from '@/components/Shared/Utils/UICard';
import Utils from '@/components/Shared/Utils';
import { LoadingBackdrop } from '@/components/Layout/DefaultLayout';

// Form validation
const suite = create('customer_form', (data = {}) => {
  test('name', 'Customer name is required', () => {
    enforce(data.name).isNotEmpty();
  });

  test('email', 'Email is required', () => {
    enforce(data.email).isNotEmpty();
  });

  test('email', 'Email must be valid', () => {
    if (data.email) {
      enforce(data.email).matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
    }
  });

  test('plan', 'Plan is required', () => {
    enforce(data.plan).isNotEmpty();
  });
});

interface CustomerFormValues {
  name: string;
  email: string;
  phone: string;
  plan: string;
  status: string;
  contactPerson: string;
  address: string;
  country: string;
  products: {
    cloudServer: boolean;
    vps: boolean;
    storage: boolean;
    cdn: boolean;
  };
  notes: string;
}

// Mock function to get customer data - replace with API call
const getCustomerById = (id: string) => {
  return {
    id,
    name: 'Acme Corporation',
    email: 'contact@acme.com',
    phone: '+1 (555) 123-4567',
    status: 'Active',
    plan: 'Enterprise',
    contactPerson: 'John Smith',
    address: '123 Business Park, San Francisco, CA 94103',
    country: 'United States',
    products: {
      cloudServer: true,
      vps: true,
      storage: true,
      cdn: false
    },
    notes: 'Key account with growing infrastructure needs. Looking to expand cloud server usage next quarter.'
  };
};

export default function CustomerEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  
  if (!id) {
    navigate('/resellers/customers');
    return null;
  }
  
  const methods = useForm<CustomerFormValues>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      plan: '',
      status: '',
      contactPerson: '',
      address: '',
      country: '',
      products: {
        cloudServer: false,
        vps: false,
        storage: false,
        cdn: false
      },
      notes: ''
    },
    resolver: vestResolver(suite)
  });
  
  const { control, handleSubmit, formState: { errors }, reset, watch } = methods;
  
  // Load customer data on component mount
  useEffect(() => {
    const customerData = getCustomerById(id);
    reset({
      name: customerData.name,
      email: customerData.email,
      phone: customerData.phone,
      plan: customerData.plan,
      status: customerData.status,
      contactPerson: customerData.contactPerson,
      address: customerData.address,
      country: customerData.country,
      products: customerData.products,
      notes: customerData.notes
    });
  }, [id, reset]);

  const countries = ['United States', 'Singapore', 'Malaysia', 'Indonesia', 'Vietnam', 'Japan', 'South Korea', 'Germany', 'United Kingdom', 'France'];
  const planOptions = ['Business', 'Enterprise', 'Custom'];
  const statusOptions = ['Active', 'Pending', 'Suspended'];

  const onSubmit = (data: CustomerFormValues) => {
    console.log('Submitted data:', data);
    // API call would go here
    setSuccessMessage('Customer updated successfully');
    
    // Optionally redirect after a delay
    setTimeout(() => {
      navigate(`/resellers/customers/detail/${id}`);
    }, 1500);
  };

  const customerProducts = watch('products');

  const sidebar = React.useMemo(() => ({
    name: '@Reseller Portal',
    uuid: '',
    provider: '',
    paths: [
      {
        name: 'Dashboard',
        key: 'dashboard',
        slug: '/resellers',
        icon: null,
        disabled: false,
        beta: false,
      },
      {
        name: 'Customers',
        key: 'customers',
        slug: '/resellers/customers',
        icon: null,
        disabled: false,
        beta: false,
      },
      {
        name: 'Products',
        key: 'products',
        slug: '/resellers/products',
        icon: null,
        disabled: false,
        beta: false,
      },
      {
        name: 'Billing',
        key: 'billing',
        slug: '/resellers/billing',
        icon: null,
        disabled: false,
        beta: false,
      },
      {
        name: 'Reports',
        key: 'reports',
        slug: '/resellers/reports',
        icon: null,
        disabled: false,
        beta: false,
      }
    ]
  }), []);

  return (
    <Grid container>
      <Utils.LayoutUtils.MainSidebar {...sidebar} />
      <Utils.LayoutUtils.MainContainer background="white">
        <React.Suspense fallback={<LoadingBackdrop />}>
          <Utils.LayoutUtils.MainWrapper>
            {/* Breadcrumbs */}
            <Box mb={3}>
              <Utils.LayoutUtils.MainBreadcrumbs
                paths={[
                  { name: 'Reseller Dashboard', path: '/resellers' },
                  { name: 'Customers', path: '/resellers/customers' },
                  { name: 'Edit Customer', path: '' }
                ]}
              />
            </Box>
            
            <Stack direction="row" justifyContent="space-between" mb={4}>
              <Box my="auto">
                <Utils.LayoutUtils.MainHeading
                  title="Edit Customer"
                  description={`Update information for customer ${id}`}
                />
              </Box>
            </Stack>
            
            {successMessage && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {successMessage}
              </Alert>
            )}
            
            <FormProvider {...methods}>
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <UICard>
                      <UICardHeading
                        title="Customer Information"
                        description="Update the customer's business details"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth error={!!errors.name}>
                                    <Utils.FormUtils.PrimaryInput
                                      {...field}
                                      label="Business Name"
                                      placeholder="Enter customer business name"
                                      error={!!errors.name}
                                    />
                                    {errors.name && (
                                      <FormHelperText>{errors.name.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Controller
                                name="email"
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth error={!!errors.email}>
                                    <Utils.FormUtils.PrimaryInput
                                      {...field}
                                      label="Business Email"
                                      placeholder="Enter customer email address"
                                      error={!!errors.email}
                                    />
                                    {errors.email && (
                                      <FormHelperText>{errors.email.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Controller
                                name="phone"
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth>
                                    <Utils.FormUtils.PrimaryInput
                                      {...field}
                                      label="Phone Number"
                                      placeholder="Enter customer phone number"
                                    />
                                  </FormControl>
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Controller
                                name="contactPerson"
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth>
                                    <Utils.FormUtils.PrimaryInput
                                      {...field}
                                      label="Contact Person"
                                      placeholder="Enter primary contact name"
                                    />
                                  </FormControl>
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Controller
                                name="country"
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth>
                                    <Utils.FormUtils.PrimarySelect
                                      {...field}
                                      fullWidth
                                      size="small"
                                      label="Country"
                                    >
                                      {countries.map((country) => (
                                        <Utils.FormUtils.PrimarySelectItem key={country} value={country}>
                                          {country}
                                        </Utils.FormUtils.PrimarySelectItem>
                                      ))}
                                    </Utils.FormUtils.PrimarySelect>
                                  </FormControl>
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Controller
                                name="address"
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth>
                                    <Utils.FormUtils.PrimaryInput
                                      {...field}
                                      label="Address"
                                      placeholder="Enter customer business address"
                                      multiline
                                      rows={3}
                                    />
                                  </FormControl>
                                )}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>
                    
                    <UICard sx={{ mt: 3 }}>
                      <UICardHeading
                        title="Product Selection"
                        description="Update the products this customer has access to"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Controller
                                name="products.cloudServer"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                  <FormControlLabel
                                    control={
                                      <Checkbox 
                                        checked={value} 
                                        onChange={onChange}
                                        sx={{
                                          '&.Mui-checked': {
                                            color: blue[600],
                                          },
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography fontSize={14}>
                                        Cloud Server
                                      </Typography>
                                    }
                                  />
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Controller
                                name="products.vps"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                  <FormControlLabel
                                    control={
                                      <Checkbox 
                                        checked={value} 
                                        onChange={onChange}
                                        sx={{
                                          '&.Mui-checked': {
                                            color: blue[600],
                                          },
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography fontSize={14}>
                                        VPS
                                      </Typography>
                                    }
                                  />
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Controller
                                name="products.storage"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                  <FormControlLabel
                                    control={
                                      <Checkbox 
                                        checked={value} 
                                        onChange={onChange}
                                        sx={{
                                          '&.Mui-checked': {
                                            color: blue[600],
                                          },
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography fontSize={14}>
                                        Storage
                                      </Typography>
                                    }
                                  />
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Controller
                                name="products.cdn"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                  <FormControlLabel
                                    control={
                                      <Checkbox 
                                        checked={value} 
                                        onChange={onChange}
                                        sx={{
                                          '&.Mui-checked': {
                                            color: blue[600],
                                          },
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography fontSize={14}>
                                        CDN
                                      </Typography>
                                    }
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                          
                          <Box mt={2}>
                            <Typography fontSize={13} color={grey[600]}>
                              Selected products ({Object.values(customerProducts).filter(Boolean).length}):
                            </Typography>
                            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                              {customerProducts.cloudServer && (
                                <Chip 
                                  label="Cloud Server" 
                                  size="small" 
                                  sx={{ fontSize: 12, mb: 1 }}
                                />
                              )}
                              {customerProducts.vps && (
                                <Chip 
                                  label="VPS" 
                                  size="small" 
                                  sx={{ fontSize: 12, mb: 1 }}
                                />
                              )}
                              {customerProducts.storage && (
                                <Chip 
                                  label="Storage" 
                                  size="small" 
                                  sx={{ fontSize: 12, mb: 1 }}
                                />
                              )}
                              {customerProducts.cdn && (
                                <Chip 
                                  label="CDN" 
                                  size="small" 
                                  sx={{ fontSize: 12, mb: 1 }}
                                />
                              )}
                              {Object.values(customerProducts).filter(Boolean).length === 0 && (
                                <Typography fontSize={13} color={grey[500]}>No products selected</Typography>
                              )}
                            </Stack>
                          </Box>
                        </Box>
                      </UICardContent>
                    </UICard>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <UICard>
                      <UICardHeading
                        title="Account Settings"
                        description="Update customer account settings"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <Controller
                                name="plan"
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth error={!!errors.plan}>
                                    <Utils.FormUtils.PrimarySelect
                                      {...field}
                                      fullWidth
                                      size="small"
                                      label="Plan"
                                      error={!!errors.plan}
                                    >
                                      {planOptions.map((plan) => (
                                        <Utils.FormUtils.PrimarySelectItem key={plan} value={plan}>
                                          {plan}
                                        </Utils.FormUtils.PrimarySelectItem>
                                      ))}
                                    </Utils.FormUtils.PrimarySelect>
                                    {errors.plan && (
                                      <FormHelperText>{errors.plan.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Controller
                                name="status"
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth>
                                    <Utils.FormUtils.PrimarySelect
                                      {...field}
                                      fullWidth
                                      size="small"
                                      label="Status"
                                    >
                                      {statusOptions.map((status) => (
                                        <Utils.FormUtils.PrimarySelectItem key={status} value={status}>
                                          {status}
                                        </Utils.FormUtils.PrimarySelectItem>
                                      ))}
                                    </Utils.FormUtils.PrimarySelect>
                                  </FormControl>
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Controller
                                name="notes"
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth>
                                    <Utils.FormUtils.PrimaryInput
                                      {...field}
                                      label="Notes"
                                      placeholder="Additional notes about this customer"
                                      multiline
                                      rows={5}
                                    />
                                  </FormControl>
                                )}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <UICard>
                      <UICardContent>
                        <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
                          <NavLink 
                            to={`/resellers/customers/detail/${id}`} 
                            style={{ textDecoration: 'none' }}
                          >
                            <Utils.ActionButton variant="outline">
                              Cancel
                            </Utils.ActionButton>
                          </NavLink>
                          
                          <Utils.FormUtils.PrimaryButton
                            type="submit"
                            variant="contained"
                          >
                            Update Customer
                          </Utils.FormUtils.PrimaryButton>
                        </Box>
                      </UICardContent>
                    </UICard>
                  </Grid>
                </Grid>
              </Box>
            </FormProvider>
          </Utils.LayoutUtils.MainWrapper>
        </React.Suspense>
      </Utils.LayoutUtils.MainContainer>
    </Grid>
  );
} 